/**
 * Calendar-date helpers pinned to Boulder (Mountain) time.
 *
 * BSides Boulder is a Mountain-Time conference, so a date-only value like
 * "2026-06-10" means the Boulder calendar day June 10 — not midnight UTC.
 * Parsing such a string with `new Date("2026-06-10")` yields midnight **UTC**,
 * which in Mountain Time is the evening of June 9, so naive date math reads
 * every due date a day early and countdowns disagree depending on whether they
 * run on the server (UTC) or in the browser. These helpers avoid that by
 * treating date-only values as plain calendar dates and deriving "today" in the
 * conference timezone, so every screen agrees.
 */

import { getDevTodayOverride } from './devClock';

export const CONFERENCE_TZ = 'America/Denver';

/** Extract the YYYY-MM-DD calendar part of a date-only or ISO string. */
export function toYMD(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** Today's calendar date in the conference timezone, as YYYY-MM-DD. */
export function todayYMD(tz: string = CONFERENCE_TZ): string {
  // Dev-only simulated date (no-op in prod) so date-driven UI like the CFP
  // timeline can be tested as if it were another day. See lib/devClock.ts.
  const override = getDevTodayOverride();
  if (override) return override;
  // 'en-CA' formats as YYYY-MM-DD.
  return new Date().toLocaleDateString('en-CA', { timeZone: tz });
}

/**
 * Whole calendar days from date `a` to date `b` (b − a), both YYYY-MM-DD.
 * Built on Date.UTC for both endpoints, so there is no timezone/DST drift —
 * it is a pure calendar-day count.
 */
export function daysBetweenYMD(a: string, b: string): number {
  const pa = toYMD(a);
  const pb = toYMD(b);
  if (!pa || !pb) return NaN;
  const [ay, am, ad] = pa.split('-').map(Number);
  const [by, bm, bd] = pb.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/**
 * Whole calendar days from today (Boulder) to a date-only value.
 * Positive = future, 0 = today, negative = past. NaN if unparseable.
 */
export function daysFromToday(dateOnly: string | null | undefined): number {
  const ymd = toYMD(dateOnly);
  if (!ymd) return NaN;
  return daysBetweenYMD(todayYMD(), ymd);
}

/** True when a date-only due date falls before today (Boulder). */
export function isPastYMD(dateOnly: string | null | undefined): boolean {
  const ymd = toYMD(dateOnly);
  if (!ymd) return false;
  // Compare via the numeric day count rather than a raw string compare, so we
  // don't depend on todayYMD() being zero-padded (true for en-CA, but this is
  // assumption-free). > 0 means today is after the due date.
  return daysBetweenYMD(ymd, todayYMD()) > 0;
}

/**
 * Format a date-only value as a short label (e.g. "Jun 10") without timezone
 * drift — the stored calendar day is rendered as-is, never shifted.
 */
export function formatYMDShort(
  dateOnly: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' },
): string {
  const ymd = toYMD(dateOnly);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}
